import moment from "moment";
import { pick } from "lodash";
import { Toastr } from "../../../revvComponents";

// Internal Imports
import { GRID, PRICEBOOK } from "../../../constants";

// Destructuring
const { PRICING_METHODS, DEALTABLE_COLUMNS } = GRID;
const {
  PRICEBOOK_FORM_DATA: PB_INFO,
  PRICING_METHOD_FLATPRICE_DATA: PB_PRICE,
  POSSIBLE_REQUIRED_FIELDS,
  PRICING_TYPES_MAP,
} = PRICEBOOK;

// Declarations
const ADHOC = "adhoc";
const BASIC = "basic";
const NON_LINEAR = "non-linear";

export const createCustomJSON = (constJSON, columnDefs, customColumnDefs) => {
  let formField;
  const customJSON = constJSON.map(item => {
    formField = columnDefs.find(def => def.name === item.name);
    if (formField) {
      // Overriding frontend json values with backend values
      item.fld = formField.field;
      if (item.name !== "price") {
        item.label = formField.headerName;
      }
      if (item.type === "dateColumn") {
        if (item.defaultVal.currentDate === true) {
          item.value = moment(Date()).format();
        } else {
          item.value = moment(Date())
            .add(item.defaultVal.increment, item.defaultVal.incrementBy)
            .format();
        }
      } else {
        item.value = item.defaultVal;
      }
    }
    return item;
  });

  let newCustomColumnDefs = [];
  if (customColumnDefs && customColumnDefs.length !== 0) {
    newCustomColumnDefs = customColumnDefs.map(item => {
      item.standard = false;
      item.defaultVal = "";
      item.span = "half";
      item.visibility = true;
      item.order = 0;
      item.fld = item.field;
      item.applicable = true;
      item.columnType = item.columnType;
      return item;
    });
  }

  return [...customJSON, ...newCustomColumnDefs];
};

// if this function returns true => validation failed
const validateRequiredFields = (fieldMapIndex, formRowData, requiredFields) =>
  POSSIBLE_REQUIRED_FIELDS.some(item => {
    if (
      requiredFields &&
      requiredFields.includes(item) &&
      formRowData[fieldMapIndex[item]] === ""
    ) {
      return true;
    }
    return false;
  });

export const validateItemForm = (
  fieldMapIndex,
  formRowData,
  requiredFields,
) => {
  if (validateRequiredFields(fieldMapIndex, formRowData, requiredFields)) {
    return "RequiredFieldsWarning";
  }

  // rule: Expiry date can't be before effective date
  if (
    moment(formRowData[fieldMapIndex.expiry_date]).isBefore(
      formRowData[fieldMapIndex.effective_date],
      "date",
    )
  ) {
    return "Effective Date should be before Expiry Date";
  }

  // other validation rules below
  // rule: Default qty can't be less than minimum qty
  const defQty = formRowData[fieldMapIndex.default_qty] || 0;
  const minQty = formRowData[fieldMapIndex.min_qty] || 0;
  if (defQty < minQty) {
    return "Default Quantity should be greater than or equal to Minimum Quantity";
  }

  return "";
};

const validateFlatPrice = (view, data, fieldMapIndex, formRowData) => {
  const price = formRowData[fieldMapIndex.price];
  const { includeSkuInPercentageOfTotal } = data;
  if (price && (price.value || price.value === 0)) {
    const obj = {
      includeSkuInPercentageOfTotal,
      type: view,
      value: parseFloat(price.value),
    };
    return obj;
  }

  Toastr.showToastr({
    type: "warning",
    message: "Please fill in the required fields.",
    timeout: 2000,
  });
  return null;
};

const validateMatrixPricing = (view, data, fieldMapIndex, formRowData) => {
  const { includeSkuInPercentageOfTotal } = data;
  const { basePrice, range } = data.matrix;

  if (
    !formRowData[fieldMapIndex.quantity_uom] ||
    formRowData[fieldMapIndex.quantity_uom] === ""
  ) {
    Toastr.showToastr({
      type: "warning",
      message: "Kindly select quantity uom",
      timeout: 2000,
    });
    return null;
  }

  if (
    range[range.length - 1].max !== "" &&
    range[range.length - 1].max < range[range.length - 1].min
  ) {
    Toastr.showToastr({
      type: "warning",
      message: "Kindly enter valid range",
      timeout: 2000,
    });
    return null;
  }

  if (range[range.length - 1].value !== "") {
    // Handle float value inside range arrayOfObj
    range.forEach(item => {
      item.value = parseFloat(item.value);
    });

    const obj = {
      basePrice: basePrice === "" ? 0 : parseFloat(basePrice),
      includeSkuInPercentageOfTotal,
      range,
      type: view,
    };
    return obj;
  }
  Toastr.showToastr({
    type: "warning",
    message: "Kindly enter price per unit",
    timeout: 2000,
  });
  return null;
};

const validateBundlePricing = (view, data) => {
  const { includeSkuInPercentageOfTotal } = data;
  const { basePrice, bundleType, bundles, isAdhoc, value } = data.bundle;
  if (bundles.length > 0) {
    switch (bundleType) {
      case BASIC:
        if (bundles.every(bundle => bundle.months !== "")) {
          bundles.forEach(item => {
            delete item.multiplySku;
          }); // multiplySku key is not needed for this type
          return {
            basePrice: basePrice === "" ? 0 : parseFloat(basePrice),
            bundles,
            includeSkuInPercentageOfTotal,
            isAdhoc,
            type: view,
            value: parseFloat(value),
          };
        }
        break;

      case ADHOC:
        bundles.forEach(item => {
          delete item.multiplySku;
        }); // multiplySku key is not needed for this type
        return {
          basePrice: basePrice === "" ? 0 : parseFloat(basePrice),
          bundles,
          includeSkuInPercentageOfTotal,
          isAdhoc,
          type: view,
          value: parseFloat(value),
        };

      case NON_LINEAR:
        if (bundles.every(bundle => bundle.months !== "")) {
          return {
            basePrice: basePrice === "" ? 0 : parseFloat(basePrice),
            bundles,
            includeSkuInPercentageOfTotal,
            type: "Non Linear", // Backend need this as a seperate type not as a sub-type
          };
        }
        break;
      default:
        break;
    }
  }

  Toastr.showToastr({
    type: "warning",
    message:
      bundles.length === 0
        ? "Please select your products/SKU."
        : "Kindly fill Scale field.",
    timeout: 2000,
  });
  return null;
};

const validatePercentageOfTotal = (view, data) => {
  const { value, priceListItems = [], sourceColumn } = data.percentageOfTotal;
  if (value && priceListItems.length > 0) {
    const obj = {
      destinationColumn: DEALTABLE_COLUMNS.EXT_BASE_PRICE,
      priceListItems,
      sourceColumn,
      type: view,
      value: parseFloat(value),
    };
    return obj;
  }
  Toastr.showToastr({
    type: "warning",
    message:
      priceListItems.length === 0
        ? "Kindly add some products"
        : "Kindly enter percentage valued",
    timeout: 2000,
  });
  return null;
};

const validateDIYPricing = (view, data) => {
  const { includeSkuInPercentageOfTotal } = data;
  const { quantityUom, minPrice } = data.diyPricing;

  const obj = {
    includeSkuInPercentageOfTotal,
    minPrice: parseFloat(minPrice || 0),
    quantityUom,
    type: view,
  };
  // Remove qtyUom key if its empty
  if (obj.quantityUom === "") {
    delete obj.quantityUom;
  }
  return obj;
};

export const validatePricingMethods = (
  view,
  data,
  fieldMapIndex,
  formRowData,
) => {
  switch (view) {
    case PRICING_METHODS.FLAT_PRICE:
      return validateFlatPrice(view, data, fieldMapIndex, formRowData);
    case PRICING_METHODS.STEP_TIER_PRICING:
    case PRICING_METHODS.TIER_PRICING:
    case PRICING_METHODS.VOLUME_PRICING:
      return validateMatrixPricing(view, data, fieldMapIndex, formRowData);
    case PRICING_METHODS.BUNDLE_PRICING:
      return validateBundlePricing(view, data);
    case PRICING_METHODS.PERCENTAGE_OF_TOTAL:
      return validatePercentageOfTotal(view, data);
    case PRICING_METHODS.DIY_PRICING:
      return validateDIYPricing(view, data);
    default:
      return false;
  }
};

export const stripDownFormData = (formRowData, apiRequiredFields, isEdit) => {
  if (!isEdit) return formRowData;
  return pick(formRowData, apiRequiredFields);
};

/* currently this method is setting fields of number type to their defaultValues
if left empty as leaving them empty makes them an empty string instead of 0/1 or
whatever their default numerical value
*/
export const rectifyDefaultValues = (formRowData, fieldMapIndex) => {
  const mergedJSON = [...PB_INFO, ...PB_PRICE];
  Object.keys(formRowData).forEach(key => {
    if (formRowData[key] === "") {
      const field = mergedJSON.find(item => item.fld === key);
      if (field && field.type === "numberColumn") {
        formRowData[key] = field.defaultVal;
      }
    }
  });

  // Rewrite - temporary solution for sending MAX_USAGE_UOM as '' if field disabled. do this somewehre else as ther may be more such cases
  if (
    formRowData[fieldMapIndex.max_usage] === 0 ||
    formRowData[fieldMapIndex.max_usage] === ""
  ) {
    formRowData[fieldMapIndex.max_usage_uom] = "";
  }

  return formRowData;
};

export const enhanceFormData = (
  formRowData,
  apiRequiredFields,
  isEdit,
  fieldMapIndex,
) =>
  // let tempObj = {};
  // tempObj = stripDownFormData(formRowData, apiRequiredFields, isEdit);
  rectifyDefaultValues(formRowData, fieldMapIndex);

/* currently this method is setting fields of number type to their defaultValues
if left empty as leaving them empty makes them an empty string instead of 0/1 or
whatever their default numerical value
*/
export const generatePricingMethodPayload = (data, fieldMapIndex) => {
  const pricingMethod =
    (data[fieldMapIndex.price] && data[fieldMapIndex.price].type) ||
    PRICING_METHODS.FLAT_PRICE;

  const payload = { ...data[fieldMapIndex.price] };
  // Bundle now has SubTypes, hence to handle fallback & new logic
  // the following codes are added.
  if (pricingMethod === PRICING_METHODS.BUNDLE_PRICING && !payload.bundleType) {
    payload.bundleType = payload.isAdhoc ? "adhoc" : "basic";
  }
  // Since the payload has "Non Linear" & radio btn alias checks for "non-linear" in BundleRow.js
  if (pricingMethod === PRICING_METHODS.NON_LINEAR) {
    payload.bundleType = "non-linear";
  }

  // Code to remove unwanted keys from the payload obj
  const { includeSkuInPercentageOfTotal } = payload;
  delete payload.includeSkuInPercentageOfTotal; // remove the key from payload as it will be returned as a seperate key @ sibling level

  return {
    includeSkuInPercentageOfTotal,
    [PRICING_TYPES_MAP[pricingMethod]]: payload,
  };
};
